import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { formatDate, formatToZMW, handleExcel, handlePrint, rootUrl } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { FaChartLine, FaEye, FaEyeSlash, FaFileExcel, FaFilePdf, FaFilter, FaPrint, FaSpinner, FaTimes, FaToggleOff, FaToggleOn } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const sumOfArray = (arr) => arr.reduce((acc, num) => acc + num, 0);
const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed()}%` : '0%');
const pad3 = (v) => String(v ?? '').padStart(3, '0');
const getMonthDifference = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
};

const programGroups = [
  { id: '151', name: 'Local taxes/rates' }, { id: '152', name: 'Fees and Charges' }, { id: '153', name: 'Licenses' },
  { id: '154', name: 'Levies' }, { id: '155', name: 'Permits' }, { id: '156', name: 'Land Charges' }, { id: '157', name: 'Other Incomes' },
  { id: '158', name: 'National Support (Grants)' }, { id: '159', name: 'Donor Support' }, { id: '111', name: 'Total' },
  { id: '1', name: 'Constituency Development' }, { id: '2', name: 'Local Governance' }, { id: '3', name: 'Integrated Development Planning' },
  { id: '4', name: 'Economic and Business Development' }, { id: '5', name: 'Public Health and Environmental Protection' },
  { id: '6', name: 'Housing and Community Amenities' }, { id: '7', name: 'Recreation Culture and Religion' }, { id: '8', name: 'Education and Skills Development' },
  { id: '9', name: 'Social Protection' }, { id: '10', name: 'Public Order and Safety' }, { id: '11', name: 'Management and Support Services' },
  { id: '12', name: 'Resource Mobilisation and Management' }, { id: '13', name: 'District Health servcies' }, { id: '14', name: 'Veterinary services' },
  { id: '15', name: 'Transport Services' }, { id: '16', name: 'Agricultural Services ' }, { id: '17', name: 'Fisheries and Livestock' },
  { id: '18', name: 'Social Protection and Community Development' }, { id: '000', name: 'Rest' }
];

export default function ReceiptsBudget({ startDate, endDate, triggerFetch }) {
  const [summaryByProgramSub, setSummaryByProgramSub] = useState({});
  const [cashBooks, setCashBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [modal, setModal] = useState({ open: false, title: '', rows: [], total: 0 });
  const [modalLoading, setModalLoading] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    accountCode: true,
    accountName: true,
    totalBudget: true,
    flexed: true,
    actual: true,
    actualToDate: true,
    performance: true,
    difference: true,
    budgetPerformance: true
  });

  const { allData } = useOutletContext();
  const auth = useAuth();
  const componentRef = useRef(null);
  const reportYear = useMemo(() => new Date(startDate).getFullYear(), [startDate]);
  const months = useMemo(() => getMonthDifference(startDate, endDate) + 1, [startDate, endDate]);

  useEffect(() => {
    if (!triggerFetch || !startDate || !endDate) return;
    setLoading(true);
    const bankFilter = cashBooks.length ? cashBooks.join(',') : 'all';
    const payload = `${auth.user?.id}~${startDate}~${endDate}~${bankFilter}`;
    axios.get(rootUrl(`/cashbookSummary/${encodeURIComponent(payload)}`))
      .then((res) => {
        const rows = res.data?.data?.byProgramSub || [];
        const map = {};
        rows.forEach((r) => {
          const key = `${r.program_code}-${r.subprogram_code}`;
          map[key] = {
            month_total: parseFloat(r.month_total) || 0,
            period_total: parseFloat(r.period_total) || 0
          };
        });
        setSummaryByProgramSub(map);
      })
      .catch((e) => {
        console.error(e);
        toast.error('Error fetching report data');
      })
      .finally(() => setLoading(false));
  }, [triggerFetch, startDate, endDate, auth.user?.id, cashBooks]);

  const bankOptions = (allData?.bank || []).map((b) => ({ value: b.id, label: b.name }));
  const bankName = (id) => allData?.bank?.find((b) => String(b.id) === String(id))?.name || id || '-';
  const getPayeeName = (row) => {
    const payeeId = row?.paye ?? row?.payee ?? row?.payee_id ?? row?.supplier_id ?? row?.sid;
    if (!payeeId) return row?.payeeName || '-';
    return allData?.suppliers?.find((supplier) => String(supplier.id) === String(payeeId))?.name || row?.payeeName || '-';
  };
  const grouped = useMemo(() => {
    const budgetRows = (allData?.budget || []).filter(
      (b) => Number(b.year) === Number(reportYear) && b.program && b.subprogram
    );
    const map = {};

    budgetRows.forEach((b) => {
      const key = `${b.program}-${b.subprogram}`;
      if (!map[key]) {
        map[key] = {
          key,
          parentProgram: String(b.program),
          subProgram: String(b.subprogram),
          code: `${pad3(b.program)}${pad3(b.subprogram)}`,
          name:
            allData?.subProgram?.find(
              (sp) => String(sp.programCode) === String(b.program) && String(sp.subProgramCode) === String(b.subprogram)
            )?.name || `Program ${b.program} / Subprogram ${b.subprogram}`,
          totalBudget: 0,
          flexed: 0,
          actual: 0,
          actualToDate: 0,
          transactions: []
        };
      }
      map[key].totalBudget += parseFloat(b.price * b.frequency * b.qty) || 0;
    });

    Object.keys(map).forEach((key) => {
      const summary = summaryByProgramSub[key] || { month_total: 0, period_total: 0 };
      map[key].actual = summary.month_total;
      map[key].actualToDate = summary.period_total;
    });

    Object.values(map).forEach((entry) => {
      entry.flexed = (months / 12) * entry.totalBudget;
    });

    return Object.values(map).sort((a, b) => a.code.localeCompare(b.code));
  }, [allData?.budget, allData?.subProgram, reportYear, months, summaryByProgramSub]);

  const openModal = async (title, programCode, subProgramCode) => {
    try {
      setModal({ open: true, title, rows: [], total: 0 });
      setModalLoading(true);
      const bankFilter = cashBooks.length ? cashBooks.join(',') : 'all';
      const code = `${programCode}-${subProgramCode}`;
      const payload = `${auth.user?.id}~${startDate}~${endDate}~programsub~${code}~${bankFilter}`;
      const response = await axios.get(rootUrl(`/cashbookCodeTransactions/${encodeURIComponent(payload)}`));
      const txns = response.data?.data?.transactions || [];
      const sorted = [...txns].sort((a, b) => new Date(a.date) - new Date(b.date) || Number(a.id) - Number(b.id));
      let running = 0;
      const normalized = sorted.map((r) => {
        const amount = parseFloat(r.amount) || 0;
        running += r.type === 'expense' ? -amount : amount;
        return { ...r, amount, runningBalance: running };
      });
      setModal({ open: true, title, rows: normalized, total: sumOfArray(normalized.map((r) => r.amount)) });
    } catch (error) {
      console.error(error);
      toast.error('Error fetching transaction details');
      setModal({ open: true, title, rows: [], total: 0 });
    } finally {
      setModalLoading(false);
    }
  };

  const renderRows = () => {
    const out = [];
    let grandBudget = 0;
    let grandFlexed = 0;
    let grandActual = 0;
    let grandToDate = 0;

    programGroups.forEach((g) => {
      const list = grouped.filter((r) => String(r.parentProgram) === String(g.id));
      if (list.length === 0 && g.id !== '111' && g.id !== '000') return;

      let sBudget = 0;
      let sFlexed = 0;
      let sActual = 0;
      let sToDate = 0;

      list.forEach((r) => {
        sBudget += r.totalBudget;
        sFlexed += r.flexed;
        sActual += r.actual;
        sToDate += r.actualToDate;
        if (!toggle) {
          out.push(
            <tr key={r.key} className="hover:bg-gray-50 border-b border-gray-200">
              {columnVisibility.accountCode && <td className="border border-gray-200 py-2.5 px-4 text-sm"><button className="text-blue-700 hover:underline font-semibold" onClick={() => openModal(`Program ${r.code} - ${r.name}`, r.parentProgram, r.subProgram)}>{r.code}</button></td>}
              {columnVisibility.accountName && <td className="border border-gray-200 py-2.5 px-4 text-sm">{r.name}</td>}
              {columnVisibility.totalBudget && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(r.totalBudget)}</td>}
              {columnVisibility.flexed && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(r.flexed)}</td>}
              {columnVisibility.actual && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(r.actual)}</td>}
              {columnVisibility.actualToDate && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right font-medium">{formatToZMW(r.actualToDate)}</td>}
              {columnVisibility.performance && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{pct(r.actualToDate, r.flexed)}</td>}
              {columnVisibility.difference && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{formatToZMW(r.totalBudget - r.actualToDate)}</td>}
              {columnVisibility.budgetPerformance && <td className="border border-gray-200 py-2.5 px-4 text-sm text-right">{pct(r.actualToDate, r.totalBudget)}</td>}
            </tr>
          );
        }
      });

      if (list.length > 0) {
        out.push(
          <tr key={`${g.id}-total`} className={`${!toggle ? 'bg-gray-800 text-white' : 'bg-gray-100'} border-t-2 border-gray-400`}>
            {columnVisibility.accountCode && <td className="border border-gray-300 py-3 px-4 font-semibold text-sm">{pad3(g.id)}</td>}
            {columnVisibility.accountName && <td className="border border-gray-300 py-3 px-4 font-semibold text-sm">{g.name}</td>}
            {columnVisibility.totalBudget && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(sBudget)}</td>}
            {columnVisibility.flexed && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(sFlexed)}</td>}
            {columnVisibility.actual && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(sActual)}</td>}
            {columnVisibility.actualToDate && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(sToDate)}</td>}
            {columnVisibility.performance && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{pct(sToDate, sFlexed)}</td>}
            {columnVisibility.difference && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{formatToZMW(sBudget - sToDate)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-gray-300 py-3 px-4 text-right font-semibold text-sm">{pct(sToDate, sBudget)}</td>}
          </tr>
        );
      }

      grandBudget += sBudget;
      grandFlexed += sFlexed;
      grandActual += sActual;
      grandToDate += sToDate;

      if (g.id === '111') {
        out.push(
          <tr key="income-total" className="bg-gradient-to-r from-green-600 to-green-700 text-white border-t-4 border-green-800">
            <td colSpan={2} className="px-4 py-3 border border-green-500 font-bold text-base">Total Income</td>
            {columnVisibility.totalBudget && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandBudget)}</td>}
            {columnVisibility.flexed && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandFlexed)}</td>}
            {columnVisibility.actual && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandActual)}</td>}
            {columnVisibility.actualToDate && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandToDate)}</td>}
            {columnVisibility.performance && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{pct(grandToDate, grandFlexed)}</td>}
            {columnVisibility.difference && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandBudget - grandToDate)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-green-500 py-3 px-4 text-right font-bold text-base">{pct(grandToDate, grandBudget)}</td>}
          </tr>
        );
        grandBudget = 0;
        grandFlexed = 0;
        grandActual = 0;
        grandToDate = 0;
      }

      if (g.id === '000') {
        out.push(
          <tr key="expense-total" className="bg-gradient-to-r from-red-600 to-red-700 text-white border-t-4 border-red-800">
            <td colSpan={2} className="px-4 py-3 border border-red-500 font-bold text-base">Total Expenditure</td>
            {columnVisibility.totalBudget && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandBudget)}</td>}
            {columnVisibility.flexed && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandFlexed)}</td>}
            {columnVisibility.actual && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandActual)}</td>}
            {columnVisibility.actualToDate && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandToDate)}</td>}
            {columnVisibility.performance && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{pct(grandToDate, grandFlexed)}</td>}
            {columnVisibility.difference && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{formatToZMW(grandBudget - grandToDate)}</td>}
            {columnVisibility.budgetPerformance && <td className="border border-red-500 py-3 px-4 text-right font-bold text-base">{pct(grandToDate, grandBudget)}</td>}
          </tr>
        );
      }
    });
    return out;
  };

  const exportPdf = () => {
    const input = componentRef.current;
    if (!input) return;
    html2canvas(input, { scrollY: -window.scrollY }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * w) / canvas.width;
      let left = imgH;
      let pos = 0;
      pdf.addImage(imgData, 'PNG', 0, pos, w, imgH);
      left -= h;
      while (left > 0) {
        pos -= h;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, pos, w, imgH);
        left -= h;
      }
      pdf.save(`Receipts_${startDate}_${endDate}.pdf`);
    });
  };

  if (loading) {
    return <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-xl border border-gray-200"><FaSpinner size={50} className="animate-spin text-blue-950 mb-4" /><p className="text-gray-600">Loading report data...</p></div>;
  }

  return (
    <div className="cash-report space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-950 rounded-lg"><FaChartLine className="text-white text-xl" /></div>
        <div><h1 className="font-bold text-xl md:text-2xl text-gray-900">Receipts and Payments By Program</h1><p className="text-sm text-gray-500">Budget vs actual by program/subprogram</p></div>
      </div>

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2"><FaFilter className="text-gray-600" /><label className="text-sm font-medium text-gray-700">Filter by Bank:</label></div>
          <div className="flex-1 max-w-md">
            <Select options={bankOptions} placeholder="Select banks..." isClearable isMulti className="text-sm"
              value={bankOptions.filter((o) => cashBooks.includes(o.value))}
              onChange={(opts) => setCashBooks(opts ? opts.map((o) => o.value) : [])} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-300">
          <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
            {toggle ? <FaEyeSlash /> : <FaEye />}
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 text-sm" onClick={() => setToggle(!toggle)}>
              {toggle ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
              <span>{toggle ? 'Show Details' : 'Hide Details'}</span>
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Columns:</span>
            <div className="flex flex-wrap gap-2">
              {Object.keys(columnVisibility).map((col) => (
                <label key={col} className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 text-xs">
                  <input type="checkbox" checked={columnVisibility[col]} onChange={() => setColumnVisibility((p) => ({ ...p, [col]: !p[col] }))} />
                  <span className="capitalize">{col.replace(/([A-Z])/g, ' $1').trim()}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button onClick={() => handleExcel(componentRef, `Receipts_${startDate}_${endDate}`)} className="px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm flex items-center gap-2"><FaFileExcel /><span>Excel</span></button>
        <button onClick={exportPdf} className="px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm flex items-center gap-2"><FaFilePdf /><span>PDF</span></button>
        <button onClick={() => handlePrint(componentRef)} className="px-4 py-2.5 bg-gray-700 text-white rounded-lg text-sm flex items-center gap-2"><FaPrint /><span>Print</span></button>
      </div>

      <div ref={componentRef} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="text-center py-6 px-4">
          <h2 className="font-bold text-xl md:text-2xl mb-2">{allData.council[0]?.name.toUpperCase()}</h2>
          <h2 className="font-bold text-lg md:text-xl mb-3">{'Receipts and Payments by Programs'.toUpperCase()}</h2>
          <h3 className="text-base md:text-lg font-medium opacity-90">From {formatDate(startDate)} to {formatDate(endDate)}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                {columnVisibility.accountCode && <th className="border border-gray-300 py-3 px-4 text-left text-sm">Account Code</th>}
                {columnVisibility.accountName && <th className="border border-gray-300 py-3 px-4 text-left text-sm">Account Name</th>}
                {columnVisibility.totalBudget && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Total Budget</th>}
                {columnVisibility.flexed && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Flexed</th>}
                {columnVisibility.actual && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Actual</th>}
                {columnVisibility.actualToDate && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Actual To Date</th>}
                {columnVisibility.performance && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Performance</th>}
                {columnVisibility.difference && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Difference</th>}
                {columnVisibility.budgetPerformance && <th className="border border-gray-300 py-3 px-4 text-right text-sm">Budget Performance</th>}
              </tr>
            </thead>
            <tbody className="bg-white">{renderRows()}</tbody>
          </table>
        </div>
      </div>

      {modal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">{modal.title}</h3>
              <button type="button" onClick={() => setModal((p) => ({ ...p, open: false }))} className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-5 overflow-auto max-h-[70vh]">
              {modalLoading ? <div className="text-center py-10 text-gray-600">Loading transactions...</div> : modal.rows.length === 0 ? <div className="text-center py-10 text-gray-600">No transactions found for this code.</div> : (
                <table className="w-full border-collapse">
                  <thead><tr className="bg-blue-950 text-white">
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Date</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Voucher/Ref</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Type</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Description</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Bank</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm">Payee</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm">Amount</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm">Running Balance</th>
                  </tr></thead>
                  <tbody>
                    {modal.rows.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2 text-sm">{formatDate(r.date)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{r.voucher || r.cheque || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm capitalize">{r.type || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{r.description || '-'}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{bankName(r.bank)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm">{getPayeeName(r)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm text-right">{formatToZMW(r.amount)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-sm text-right">{formatToZMW(r.runningBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-gray-100 font-semibold">
                    <td colSpan={6} className="border border-gray-300 px-3 py-2 text-right">Totals</td>
                    <td className="border border-gray-300 px-3 py-2 text-right">{formatToZMW(modal.total)}</td>
                    <td className="border border-gray-300 px-3 py-2 text-right">{formatToZMW(modal.rows[modal.rows.length - 1]?.runningBalance || 0)}</td>
                  </tr></tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
